// src/pages/FrameIQDownload.js

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "react-hot-toast"; // ← FIXED IMPORT

const FrameIQDownload = () => {
  // ==================== STATE MANAGEMENT ====================

  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [batches, setBatches] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [downloadingBatch, setDownloadingBatch] = useState(null);

  const navigate = useNavigate();
  const BASE_URL = process.env.REACT_APP_API_URL;

  // ==================== EFFECTS ====================

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    if (selectedCampaign) {
      fetchBatches(selectedCampaign);
    } else {
      setBatches([]);
    }
  }, [selectedCampaign]);

  // ==================== HANDLERS ====================

  // Fetch campaigns for dropdown
  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const response = await axios.get(`${BASE_URL}/frame_iq/api/campaign/list`);
      
      if (response.data.success) {
        setCampaigns(response.data.data || []);
      } else {
        toast.error("Failed to fetch campaigns");
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Error loading campaigns: " + error.message);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Fetch batches for selected campaign - FIXED
  const fetchBatches = async (campaignName) => {
    setLoadingBatches(true);
    setBatches([]);
    
    try {
      const response = await axios.get(
        `${BASE_URL}/frame_iq/api/campaign_batch/list`,
        {
          params: {
            brief_name: campaignName
          }
        }
      );
      
      if (response.data.success) {
        const batchData = response.data.data || [];
        setBatches(batchData);
        
        // Silently handle empty batches - not an error!
        if (batchData.length === 0) {
          console.log(`No batches found for campaign: ${campaignName}`);
        }
      } else {
        toast.error("Failed to fetch batches");
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      
      // Check if it's a 404 (no batches) vs actual error
      if (error.response && error.response.status === 404) {
        // 404 is normal - campaign exists but has no batches
        setBatches([]);
        console.log(`Campaign "${campaignName}" has no batches yet`);
      } else {
        // Real error - show toast
        toast.error("Error loading batches: " + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoadingBatches(false);
    }
  };

  // Download CSV for a batch
  const handleDownloadBatch = async (campaignName, batchNumber) => {
    setDownloadingBatch(batchNumber);
    
    try {
      const response = await axios.get(
        `${BASE_URL}/frame_iq/api/campaign_batch/download`,
        {
          params: {
            brief_name: campaignName,
            batch: batchNumber
          },
          responseType: "blob",
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Create filename
      const filename = `${campaignName.replace(/[^a-z0-9]/gi, '_')}_batch_${batchNumber}.csv`;
      link.setAttribute("download", filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`✓ ${filename} downloaded successfully!`);
    } catch (error) {
      console.error("Error downloading batch:", error);
      toast.error("Error downloading CSV: " + (error.response?.data?.message || error.message));
    } finally {
      setDownloadingBatch(null);
    }
  };

  // Handle campaign selection
  const handleCampaignChange = (e) => {
    setSelectedCampaign(e.target.value);
  };

  // ==================== RENDER ====================

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col items-start gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Download Campaign CSV</h1>
            <p className="text-sm text-gray-600 mt-1">
              Select a campaign to view and download available batches
            </p>
          </div>

          <button
            onClick={() => navigate("/campaign/list")}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            ← Back to Campaign List
          </button>
        </div>
      </div>

      {/* Campaign Selection Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Campaign
        </label>
        
        <select
          value={selectedCampaign}
          onChange={handleCampaignChange}
          disabled={loadingCampaigns}
          className="w-full md:w-96 px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <option value="">
            {loadingCampaigns ? "Loading campaigns..." : "-- Select a Campaign --"}
          </option>
          {campaigns.map((campaign, index) => (
            <option key={index} value={campaign.brief_name}>
              {campaign.brief_name}
            </option>
          ))}
        </select>

        {campaigns.length === 0 && !loadingCampaigns && (
          <p className="mt-2 text-sm text-gray-500 italic">
            No campaigns available. Create a campaign first.
          </p>
        )}
      </div>

      {/* Batches Section */}
      {selectedCampaign && (
        <div className="bg-white rounded-lg shadow">
          {/* Batches Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Available Batches for "{selectedCampaign}"
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Click download to get the CSV file for each batch
            </p>
          </div>

          {/* Loading State */}
          {loadingBatches && (
            <div className="px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
              <p className="text-gray-500">Loading batches...</p>
            </div>
          )}

          {/* Batches List */}
          {!loadingBatches && batches.length > 0 && (
            <div className="divide-y divide-gray-200">
              {batches.map((batch, index) => (
                <div
                  key={index}
                  className="px-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-gray-50 transition-colors"
                >
                  {/* Batch Info */}
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      Batch {batch.batch}
                    </h3>
                    <div className="mt-1 flex flex-wrap gap-3 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Uploaded: {batch.upload_date}
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {batch.count.toLocaleString()} rows
                      </span>
                    </div>
                  </div>

                  {/* Download Button */}
                  <button
                    onClick={() => handleDownloadBatch(selectedCampaign, batch.batch)}
                    disabled={downloadingBatch === batch.batch}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap shadow-sm"
                  >
                    {downloadingBatch === batch.batch ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download CSV
                      </>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Empty State - NO ERROR TOAST */}
          {!loadingBatches && batches.length === 0 && (
            <div className="px-6 py-12 text-center">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="font-medium text-gray-900">No batches available</p>
              <p className="text-sm text-gray-500 mt-1">
                This campaign doesn't have any batches yet
              </p>
            </div>
          )}
        </div>
      )}

      {/* Instructions Card */}
      {!selectedCampaign && !loadingCampaigns && campaigns.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex gap-3">
            <svg className="w-6 h-6 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="font-medium text-blue-900 mb-2">How to Download CSV Files</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Select a campaign from the dropdown above</li>
                <li>View the list of available batches for that campaign</li>
                <li>Click "Download CSV" to download the batch data</li>
                <li>The CSV file will be saved to your downloads folder</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Card */}
      {selectedCampaign && batches.length > 0 && !loadingBatches && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Batches</p>
                <p className="text-2xl font-bold text-gray-900">{batches.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Rows</p>
                <p className="text-2xl font-bold text-gray-900">
                  {batches.reduce((sum, batch) => sum + (batch.count || 0), 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600">Latest Upload</p>
                <p className="text-lg font-bold text-gray-900">
                  {batches[0]?.upload_date || 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FrameIQDownload;